import React, { useState, useRef, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faStop,
  faTimes,
  faGripVertical,
} from "@fortawesome/free-solid-svg-icons";

/**
 * VoiceTypingPopup Component
 *
 * Large microphone popup that appears after clicking the toolbar button.
 * Voice typing runs on the browser's free Web Speech API, so no Corti
 * credit / password gating is performed here.
 */
const VoiceTypingPopup = ({
  isOpen,
  onClose,
  onStartRecording,
  onStopRecording,
  isRecording = false,
  isConnecting = false,
  error = null,
}) => {
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const popupRef = useRef(null);

  // Drag functionality
  const handleMouseDown = useCallback(
    (e) => {
      if (e.target.closest(".voice-typing-popup-drag-handle")) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      }
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse events for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleStartRecording = useCallback(() => {
    onStartRecording();
  }, [onStartRecording]);

  if (!isOpen) return null;

  return (
    <div className="voice-typing-popup-overlay">
      <div
        ref={popupRef}
        className="voice-typing-popup"
        style={{
          position: "fixed",
          left: `${position.x}px`,
          top: `${position.y}px`,
          zIndex: 9999,
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Header with drag handle */}
        <div className="voice-typing-popup-header voice-typing-popup-drag-handle">
          <div className="voice-typing-popup-drag-icon">
            <FontAwesomeIcon icon={faGripVertical} />
          </div>
          <div className="voice-typing-popup-title">Voice</div>
          <button
            className="voice-typing-popup-close"
            onClick={onClose}
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="voice-typing-popup-content">
          {/* Main microphone button */}
          <div className="voice-typing-popup-main">
            <button
              className={`voice-typing-popup-mic-button ${
                isRecording ? "recording" : ""
              } ${isConnecting ? "connecting" : ""}`}
              onClick={isRecording ? onStopRecording : handleStartRecording}
              disabled={isConnecting}
              title={isRecording ? "Stop Recording" : "Start Recording"}
              style={{
                width: "70px",
                height: "70px",
                borderRadius: "50%",
                background: isRecording
                  ? "#ef4444"
                  : isConnecting
                  ? "#f59e0b"
                  : "#3b82f6",
                color: "white",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon
                icon={isRecording ? faStop : faMicrophone}
                style={{ fontSize: "16px", color: "white" }}
              />
            </button>
          </div>

          {/* Error display */}
          {error && (
            <div className="voice-typing-popup-error">
              <span>❌ {error}</span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .voice-typing-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: transparent;
          z-index: 1000;
          pointer-events: none;
        }

        .voice-typing-popup {
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border: 1px solid #e5e7eb;
          width: 100px;
          height: 120px;
          position: relative;
          pointer-events: auto;
          user-select: none;
          display: flex;
          flex-direction: column;
        }

        .voice-typing-popup-header {
          display: flex;
          align-items: center;
          padding: 2px 6px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 8px 8px 0 0;
          cursor: move;
        }

        .voice-typing-popup-drag-icon {
          color: #9ca3af;
          font-size: 12px;
          margin-right: 8px;
          cursor: move;
        }

        .voice-typing-popup-title {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }

        .voice-typing-popup-close {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          font-size: 14px;
          transition: background-color 0.2s;
        }

        .voice-typing-popup-close:hover {
          background: #e5e7eb;
        }

        .voice-typing-popup-content {
          padding: 4px;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .voice-typing-popup-status {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
          font-size: 11px;
          color: #6b7280;
          text-align: center;
        }

        .voice-typing-popup-spinner {
          width: 12px;
          height: 12px;
          border: 2px solid #e5e7eb;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .voice-typing-popup-main {
          display: flex;
          justify-content: center;
          margin-bottom: 8px;
        }

        .voice-typing-popup-mic-button {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: #3b82f6;
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          font-size: 20px;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          border: 2px solid #2563eb;
        }

        .voice-typing-popup-mic-button:hover:not(:disabled) {
          background: #2563eb;
          transform: scale(1.05);
        }

        .voice-typing-popup-mic-button.recording {
          background: #ef4444;
          border-color: #dc2626;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
          animation: pulse 1.5s infinite;
        }

        .voice-typing-popup-mic-button.connecting {
          background: #f59e0b;
          border-color: #d97706;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
          animation: spin 1s linear infinite;
        }

        .voice-typing-popup-mic-button:disabled {
          background: #6b7280;
          border-color: #4b5563;
          cursor: not-allowed;
          transform: none;
          box-shadow: 0 2px 6px rgba(107, 114, 128, 0.3);
        }

        .voice-typing-popup-error {
          font-size: 12px;
          color: #dc2626;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 4px;
          padding: 8px;
          margin-top: 8px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default VoiceTypingPopup;
